import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/apiClient.js';
import { useApi, useMutation } from '../../lib/useApi.js';
import { useClientPagination } from '../../lib/usePagination.js';
import { toast } from '../../lib/useToast.js';
import {
  PageHeader,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  StatusChip,
  PageViewerControls,
} from '../../components/primitives.jsx';
import {
  KpiHeroBand,
  GaugeCard,
  MiniBars,
  AiCitationStrip,
  CitationSourceLink,
  citationRoute,
  StaggerReveal,
} from '../../components/index.js';
import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import {
  ProvenanceBadge,
  normalizedProvenance,
} from '../../components/ProvenanceBadge.jsx';
import { humanizeEnum } from '../../lib/fieldLabels.js';
import './ai.css';
import './ai.redesign.css';

// Mirror of the AiRunQueueCard REVIEW_TONES map so governed-run review state
// renders with the same executive traffic-light tone everywhere.
const REVIEW_TONES = {
  pending_human_review: 'amber',
  approved_for_export: 'sage',
  approve_for_export: 'sage',
  human_reviewed: 'blue',
  reject: 'brick',
  rejected: 'brick',
  needs_rework: 'amber',
};

const GUARDRAIL_TONES = {
  passed: 'sage',
  pass: 'sage',
  blocked: 'brick',
  failed: 'brick',
  fail: 'brick',
  flagged: 'amber',
  warn: 'amber',
  pending: 'slate',
};

function reviewTone(state) {
  return REVIEW_TONES[state] || 'slate';
}

function guardrailTone(outcome) {
  return GUARDRAIL_TONES[String(outcome || 'pending').toLowerCase()] || 'slate';
}

function confidenceTone(pct) {
  if (pct == null) return 'blue';
  if (pct >= 80) return 'sage';
  if (pct >= 60) return 'amber';
  return 'brick';
}

// Forecast rows the run may carry; absent data => omit the viz.
function runForecasts(run) {
  const forecasts = run?.content?.forecasts;
  return Array.isArray(forecasts) ? forecasts : [];
}

function formatRunTimestamp(value) {
  if (!value) return 'Pending';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRunOutput(run) {
  if (!run) return '(no output)';
  if (typeof run.content === 'string' && run.content.trim()) return run.content;
  if (typeof run.output === 'string' && run.output.trim()) return run.output;
  if (run.content && typeof run.content === 'object') {
    if (typeof run.content.answer === 'string' && run.content.answer.trim()) {
      return run.content.answer;
    }
    if (typeof run.content.narrative === 'string' && run.content.narrative.trim()) {
      return run.content.narrative;
    }
    if (Array.isArray(run.content.forecasts) && run.content.forecasts.length) {
      return run.content.forecasts
        .map((forecast) => {
          const projectLabel = forecast.projectName || forecast.projectId || 'Project';
          const riskPct = Math.round(Number(forecast.atRiskProbability || 0) * 100);
          return `${projectLabel}: ${riskPct}% at risk`;
        })
        .join('\n');
    }
  }
  if (typeof run.summary === 'string' && run.summary.trim()) return run.summary;
  return '(no output)';
}

function confidenceSourceLabel(source) {
  return (
    {
      heuristic: 'Heuristic confidence',
      rules: 'Rules confidence',
      model: 'Model confidence',
      authored: 'Authored confidence',
      unknown: 'Confidence · source unknown',
    }[source] || 'Confidence · source unknown'
  );
}

function formatProviderModelMode(provenance) {
  if (provenance.executionType === 'rule') {
    return `${provenance.rulesEngine.id} rules engine / v${provenance.rulesEngine.version} / no model invoked`;
  }
  if (provenance.executionType === 'mock') {
    return `${provenance.actualProvider} / simulated assistant / no model invoked`;
  }
  return `${provenance.actualProvider} / ${provenance.modelId} / ${provenance.corpusMode}`;
}

// Scope-record navigation (AC-AI-OPS-4): the dossier links to the record the
// run was scoped against. Falls back to the citation route table for scoped
// ids the map does not know, and renders inert when nothing resolves.
const SCOPE_RECORD_ROUTES = {
  portfolio: (id) => `/portfolios/${encodeURIComponent(id)}`,
  program: (id) => `/programs/${encodeURIComponent(id)}`,
  project: (id) => `/projects/${encodeURIComponent(id)}`,
  loe: (id) => `/analytics/loes/${encodeURIComponent(id)}`,
};

function scopeRecordRoute(scopeType, scopeId) {
  if (!scopeId) return null;
  const builder = SCOPE_RECORD_ROUTES[String(scopeType || '').toLowerCase()];
  if (builder) return builder(String(scopeId));
  return citationRoute(String(scopeId));
}

// Confirm-dialog copy per review action (AC-AI-OPS-3).
const REVIEW_ACTION_META = {
  approve_for_export: {
    title: 'Approve this run for export?',
    body: 'Approving records you as the named human reviewer and unlocks export of products derived from this run.',
    confirmLabel: 'Approve for export',
    tone: 'primary',
  },
  needs_rework: {
    title: 'Send this run back for rework?',
    body: 'The run returns to its requester with your feedback and stays blocked from export.',
    confirmLabel: 'Request rework',
    tone: 'primary',
  },
  reject: {
    title: 'Reject this AI run?',
    body: 'Rejecting records the decision with your name and permanently blocks this output from export.',
    confirmLabel: 'Reject run',
    tone: 'danger',
  },
};

export function AiRunDetailPage() {
  const { id } = useParams();
  const r = useApi(`/ai/runs/${id}`);
  // Products derived from this run (AC-AI-OPS-4). Roles without product access
  // simply see nothing here — the section renders only from real data.
  const productsRes = useApi('/briefing-products', { query: { limit: 200 } });
  const review = useMutation();
  const run = r.data?.data || null;
  const [localRun, setLocalRun] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    setLocalRun(run);
  }, [run]);

  const activeRun = localRun || run;
  const citationPager = useClientPagination(
    Array.isArray(activeRun?.citations) ? activeRun.citations : [],
    {
      initialPageSize: 5,
      resetKey: `${id}-${activeRun?.citations?.length || 0}`,
    },
  );

  const derivedProducts = useMemo(() => {
    const products = productsRes.data?.data || [];
    return products.filter((product) => {
      if (!product) return false;
      // AC-W7 — factory-generated briefing products carry the originating run under sourceAiRunId.
      if (product.relatedAIRunId === id || product.aiRunId === id || product.sourceAiRunId === id) {
        return true;
      }
      if (Array.isArray(product.sourceArtifactIds) && product.sourceArtifactIds.includes(id)) {
        return true;
      }
      return (
        Array.isArray(product.citations) &&
        product.citations.some((citation) => citation?.source === id)
      );
    });
  }, [productsRes.data?.data, id]);

  if (r.loading)
    return (
      <>
        <PageHeader title={`AI run ${id}`} />
        <LoadingSkeleton rows={6} />
      </>
    );
  if (r.error) return <ErrorState error={r.error} />;
  if (!activeRun) return <EmptyState title="Run not found" />;

  async function reviewRun(action, note) {
    try {
      const response = await review.run(() =>
        api.post(`/ai/runs/${id}/review`, { action, note: note || null }),
      );
      const updatedRun = response.data || response;
      setLocalRun((current) => ({ ...(current || {}), ...updatedRun }));
      toast.success({
        title: 'AI review updated',
        body: `${activeRun.id} is now ${humanizeEnum(updatedRun.reviewState || action)}.`,
      });
    } catch (error) {
      toast.error({
        title: 'Review failed',
        body: error.message,
      });
    } finally {
      setPendingAction(null);
    }
  }

  const reviewState = activeRun.reviewState || activeRun.humanReviewState || 'pending_human_review';
  const provenance = normalizedProvenance(activeRun, 'ai');
  const guardrailOutcome = activeRun.guardrailOutcome || 'pending';
  const confidencePct =
    provenance.confidence.value != null
      ? Math.round(Number(provenance.confidence.value) * 100)
      : null;
  const confidenceLabel = confidenceSourceLabel(provenance.confidence.source);
  const citationCount = activeRun.citations?.length || 0;
  const forecasts = runForecasts(activeRun);
  // Once a decision is recorded, review controls lock (AC-AI-OPS-3).
  const reviewDecided = reviewState !== 'pending_human_review';
  const scopeRoute = scopeRecordRoute(activeRun.scopeType, activeRun.scopeId);
  const pendingMeta = pendingAction ? REVIEW_ACTION_META[pendingAction] : null;

  const heroItems = [
    {
      id: 'confidence',
      label: confidenceLabel,
      value: confidencePct != null ? confidencePct : '—',
      unit: confidencePct != null ? '%' : '',
      status: confidencePct != null ? confidenceTone(confidencePct) : 'slate',
      statusLabel: confidencePct != null ? provenance.confidence.source : 'Not scored',
    },
    {
      id: 'review',
      label: 'Review state',
      value: reviewState.replace(/_/g, ' '),
      status: reviewTone(reviewState),
      statusLabel: 'Governance',
    },
    {
      id: 'guardrail',
      label: 'Guardrail',
      value: String(guardrailOutcome).replace(/_/g, ' '),
      status: guardrailTone(guardrailOutcome),
      statusLabel: 'Safety',
    },
    {
      id: 'evidence',
      label: 'Evidence',
      value: citationCount,
      unit: citationCount === 1 ? ' citation' : ' citations',
      status: citationCount > 0 ? 'blue' : 'amber',
      statusLabel: 'Sources',
    },
  ];

  return (
    <>
      <nav className="briefing-detail-crumbs" aria-label="Breadcrumb">
        <Link to="/ai/orchestration">AI Operations</Link>
        <span className="sep">›</span>
        <span>{activeRun.id}</span>
      </nav>
      <PageHeader
        eyebrow={`AI · ${activeRun.agentId || ''} · ${activeRun.id}`}
        title={activeRun.title || activeRun.runKind || activeRun.kind || activeRun.id}
        subtitle={`Status ${humanizeEnum(activeRun.status || 'completed')} · review ${humanizeEnum(reviewState)}${
          provenance.confidence.value != null
            ? ` · ${confidenceLabel.toLowerCase()} ${Number(provenance.confidence.value).toFixed(2)}`
            : ''
        }`}
      >
        <ProvenanceBadge value={activeRun} kind="ai" />
      </PageHeader>
      <KpiHeroBand
        className="ai-rd-hero"
        eyebrow="Governed AI run"
        context={`AIRUN ${activeRun.id}`}
        items={heroItems}
        columns={4}
      />
      <section className="bento">
        <div className="span-8 card">
          <div className="ai-governance-block" style={{ marginBottom: 16 }}>
            <div className="row spread" style={{ gap: 12, alignItems: 'flex-start' }}>
              <div>
                <div className="eyebrow">Governed AI record</div>
                <h2 className="card-title">Human review required</h2>
              </div>
              <StatusChip
                status={
                  activeRun.reviewState || activeRun.humanReviewState || 'pending_human_review'
                }
              />
            </div>
            <dl className="ai-detail-grid">
              <div>
                <dt>AI run ID</dt>
                <dd>{activeRun.id}</dd>
              </div>
              <div>
                <dt>Agent / capability</dt>
                <dd>{activeRun.agentId || 'Unknown agent'}</dd>
              </div>
              <div>
                <dt>Scope</dt>
                <dd>
                  {activeRun.scopeType || 'scope'}
                  {activeRun.scopeId ? (
                    <>
                      {' · '}
                      {scopeRoute ? (
                        <Link to={scopeRoute} data-testid="ai-run-scope-link">
                          {activeRun.scopeId}
                        </Link>
                      ) : (
                        <span title="No linked record view">{activeRun.scopeId}</span>
                      )}
                    </>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt>Trigger type</dt>
                <dd>{activeRun.triggerType || 'on_demand'}</dd>
              </div>
              <div>
                <dt>Provider / model / corpus</dt>
                <dd>{formatProviderModelMode(provenance)}</dd>
              </div>
              <div>
                <dt>Created by</dt>
                <dd>
                  {activeRun.createdByUserId || activeRun.requestedByUserId || 'Unknown requester'}
                </dd>
              </div>
              <div>
                <dt>Created at</dt>
                <dd>{formatRunTimestamp(activeRun.createdAt || activeRun.startedAt)}</dd>
              </div>
              <div>
                <dt>Generated</dt>
                <dd>
                  {formatRunTimestamp(
                    activeRun.generatedAt || activeRun.completedAt || activeRun.startedAt,
                  )}
                </dd>
              </div>
              <div>
                <dt>{confidenceLabel}</dt>
                <dd className="ai-rd-status-line">
                  <span className={`chip ${confidenceTone(confidencePct)} sm`}>
                    {confidencePct != null ? `${confidencePct}%` : 'N/A'}
                  </span>
                </dd>
              </div>
              <div>
                <dt>Review state</dt>
                <dd className="ai-rd-status-line">
                  <span className={`chip ${reviewTone(reviewState)} sm`} title={reviewState}>
                    {humanizeEnum(reviewState)}
                  </span>
                </dd>
              </div>
              <div>
                <dt>Guardrail outcome</dt>
                <dd className="ai-rd-status-line">
                  <span className={`chip ${guardrailTone(guardrailOutcome)} sm`} title={guardrailOutcome}>
                    {humanizeEnum(guardrailOutcome)}
                  </span>
                </dd>
              </div>
              <div>
                <dt>Evidence</dt>
                <dd>{activeRun.citations?.length || 0} citations</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{activeRun.status || 'completed'}</dd>
              </div>
            </dl>
            <div className="ai-detail-actions">
              <button
                className="btn success sm"
                onClick={() => setPendingAction('approve_for_export')}
                disabled={review.running || reviewDecided}
              >
                Approve for export
              </button>
              <button
                className="btn ghost sm"
                onClick={() => setPendingAction('needs_rework')}
                disabled={review.running || reviewDecided}
              >
                Needs rework
              </button>
              <button
                className="btn danger sm"
                onClick={() => setPendingAction('reject')}
                disabled={review.running || reviewDecided}
              >
                Reject
              </button>
            </div>
            {reviewDecided ? (
              <p className="muted small" data-testid="ai-run-review-record">
                Decision recorded
                {activeRun.reviewedByUserId ? ` by ${activeRun.reviewedByUserId}` : ''}
                {activeRun.reviewedAt ? ` on ${formatRunTimestamp(activeRun.reviewedAt)}` : ''}
                {activeRun.reviewNote ? ` — “${activeRun.reviewNote}”` : ''}. Review controls are
                locked.
              </p>
            ) : null}
          </div>
          <h2 className="card-title">Output</h2>
          {forecasts.length ? (
            <StaggerReveal className="ai-rd-forecast">
              {forecasts.map((forecast) => {
                const pct = Math.round(Number(forecast.atRiskProbability || 0) * 100);
                const label = forecast.projectName || forecast.projectId || 'Project';
                return (
                  <div
                    key={forecast.projectId || label}
                    className="ai-rd-forecast-row"
                    data-testid={`ai-rd-forecast-${forecast.projectId || label}`}
                  >
                    <span className="ai-rd-forecast-name" title={label}>
                      {label}
                    </span>
                    <span className="ai-rd-forecast-bars">
                      <MiniBars
                        values={[pct]}
                        tone={confidenceTone(pct)}
                        ariaLabel={`${label} ${pct}% at risk`}
                      />
                    </span>
                    <span className="ai-rd-forecast-pct">{pct}%</span>
                  </div>
                );
              })}
            </StaggerReveal>
          ) : (
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
              {formatRunOutput(activeRun)}
            </pre>
          )}
        </div>
        <div className="span-4 ai-rd-gauge-cell">
          {confidencePct != null ? (
            <GaugeCard
              id="run-confidence"
              value={confidencePct}
              min={0}
              max={100}
              unit="%"
              label={confidenceLabel}
              sublabel={`Governed run · ${reviewState.replace(/_/g, ' ')}`}
              tone={confidenceTone(confidencePct)}
            />
          ) : null}
          <div className="card" data-testid="ai-run-derived-products">
            <h2 className="card-title">Products derived from this run</h2>
            {derivedProducts.length ? (
              <ul className="stack tight" style={{ listStyle: 'none', padding: 0, gap: 8 }}>
                {derivedProducts.map((product) => (
                  <li key={product.id} className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <Link className="btn ghost sm" to={`/briefings/${product.id}`}>
                      {product.title || product.id}
                    </Link>
                    <StatusChip status={product.status || 'draft'} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted small">
                No briefings or papers reference this run yet. Generated products that cite it will
                link back here.
              </p>
            )}
          </div>
          <div className="card">
            <h2 className="card-title">Citations</h2>
            {Array.isArray(activeRun.citations) && activeRun.citations.length ? (
              <>
                <AiCitationStrip citations={citationPager.items} />
                <ol className="muted small" style={{ marginTop: 8 }}>
                  {citationPager.items.map((c, i) => (
                    <li key={i}>
                      <strong>
                        <CitationSourceLink id={c.source || c.title} />
                      </strong>
                      {c.excerpt ? <div className="muted small">{c.excerpt}</div> : null}
                    </li>
                  ))}
                </ol>
              </>
            ) : (
              <EmptyState title="No citations" />
            )}
            <PageViewerControls
              page={citationPager.page}
              label="citations"
              pageSize={citationPager.pageSize}
              onOffsetChange={citationPager.setOffset}
              onPageSizeChange={citationPager.setPageSize}
            />
            <div style={{ marginTop: 12 }}>
              <StatusChip status={reviewState} />
            </div>
          </div>
        </div>
      </section>
      <ConfirmActionDialog
        open={Boolean(pendingAction)}
        title={pendingMeta?.title || 'Confirm review decision'}
        body={pendingMeta?.body}
        confirmLabel={pendingMeta?.confirmLabel || 'Confirm'}
        tone={pendingMeta?.tone || 'primary'}
        showNote
        noteLabel="Review note"
        noteHint="Optional — recorded with the decision and shown on the run dossier."
        busy={review.running}
        onConfirm={(note) => reviewRun(pendingAction, note)}
        onCancel={() => setPendingAction(null)}
      />
    </>
  );
}
